import { FormatType } from '@revelio/iso-utility';

import {
  DEFAULT_GRID_COLUMNS,
  DEFAULT_GRID_ROWS,
} from '../../components/PageConfigProvider/constants';
import type { TabContent } from '../../components/PageConfigProvider/page-reducer';
import {
  type Page,
  PageType,
  RevelioPageId,
} from '../../components/PageConfigProvider/types';
import type { PositionedItem } from '../../components/PageConfigProvider/types/item';
import { FilterId } from '../../filters/types/filters';
import {
  demandSupplyRatioRankInfoText,
  marketTightnessInfoText,
  timeToFillRankInfoText,
  wagePressureRankInfoText,
} from '../market-conditions-info-text';

const items: PositionedItem[] = [
  {
    id: 'e060690f-25f2-49d5-95cb-f6f37a1f0c33',
    title: 'Market Tightness',
    isTitleManuallySet: true,
    position: { x: 0, y: 0, width: 30, height: 12 },
    info: { text: marketTightnessInfoText },
    plot: {
      chartType: 'gauge-v2',
      format: FormatType.PERCENTAGE,
      gaugeV2: {
        value: '{{VALUE "market_tightness"}}',
        minLabel: 'Favors Employers',
        maxLabel: 'Favors Employees',
      },
    },
  },
  {
    id: '88f98cdd-880b-4b96-8e1f-58f58817657b',
    title: 'Demand/Supply Ratio',
    isTitleManuallySet: true,
    position: { x: 0, y: 12, width: 10, height: 11 },
    info: { text: demandSupplyRatioRankInfoText },
    plot: {
      chartType: 'gauge-v2',
      format: FormatType.PERCENTAGE,
      gaugeV2: {
        value: '{{VALUE "demand_supply_percentile"}}',
        minLabel: 'Favors Employers',
        maxLabel: 'Favors Employees',
      },
    },
  },
  {
    id: '203fdf95-1d3f-4701-8685-238758fc1241',
    title: 'Wage Pressure',
    isTitleManuallySet: true,
    position: { x: 10, y: 12, width: 10, height: 11 },
    info: { text: wagePressureRankInfoText },
    plot: {
      chartType: 'gauge-v2',
      format: FormatType.PERCENTAGE,
      gaugeV2: {
        value: '{{VALUE "wage_pressure_percentile"}}',
        minLabel: 'Favors Employers',
        maxLabel: 'Favors Employees',
      },
    },
  },
  {
    id: '9bfcd9e8-ba84-4b50-a2fd-8c9bbde7897c',
    title: 'Time to Fill',
    isTitleManuallySet: true,
    position: { x: 20, y: 12, width: 10, height: 11 },
    info: { text: timeToFillRankInfoText },
    plot: {
      chartType: 'gauge-v2',
      format: FormatType.PERCENTAGE,
      gaugeV2: {
        value: '{{VALUE "time_to_fill_percentile"}}',
        minLabel: 'Favors Employers',
        maxLabel: 'Favors Employees',
      },
    },
  },
  {
    id: '30922e9d-a44f-42f6-94b4-5178899b6aaf',
    title: 'Demand/Supply',
    position: { x: 0, y: 23, width: 10, height: 13 },
    plot: {
      chartType: 'line',
      defaultMetricOptions: ['demand_supply'],
      format: FormatType.RATIO,
    },
  },
  {
    id: 'f6b6214d-6a91-4192-a795-94b11bad80a9',
    title: 'Wage Pressure',
    isTitleManuallySet: true,
    position: { x: 10, y: 23, width: 10, height: 13 },
    plot: {
      chartType: 'line',
      defaultMetricOptions: ['wage_pressure'],
      format: FormatType.PERCENTAGE,
    },
  },
  {
    id: 'e76f103a-2c05-4217-979c-cd529f4efeb7',
    title: 'Time to Fill',
    isTitleManuallySet: true,
    position: { x: 20, y: 23, width: 10, height: 13 },
    plot: {
      chartType: 'line',
      defaultMetricOptions: ['time_to_fill'],
      format: FormatType.INTEGER,
    },
  },
  {
    id: '20540dae-cd30-4d15-b881-a05868f32db3',
    title: 'Postings by Role',
    isTitleManuallySet: true,
    position: { x: 30, y: 0, width: 18, height: 36 },
    plot: {
      chartType: 'grouped-bar',
      secondaryDimension: 'role',
      defaultMetricOptions: ['postings_active'],
      format: FormatType.DECIMAL,
    },
  },
];

const tabContent: TabContent = {
  id: RevelioPageId.INDUSTRY_MARKET_CONDITIONS,
  primaryFilter: {
    id: FilterId.INDUSTRY,
    tagColors: true,
  },
  grid: { rows: DEFAULT_GRID_ROWS, columns: DEFAULT_GRID_COLUMNS },
  secondaryFilters: [
    FilterId.DATE_RANGE,
    FilterId.EDUCATION,
    FilterId.ETHNICITY,
    FilterId.GENDER,
    FilterId.GEOGRAPHY,
    FilterId.ROLE,
    FilterId.CUSTOM_SKILL,
    FilterId.SENIORITY,
    FilterId.KEYWORDS,
    FilterId.TITLE,
    FilterId.CONTINGENT_WORKERS,
  ].map((id) => ({ id })),
  items,
};

export const industryMarketConditions: Page = {
  type: PageType.REVELIO_PUBLIC,
  id: RevelioPageId.INDUSTRY_MARKET_CONDITIONS,
  revelioPageId: RevelioPageId.INDUSTRY_MARKET_CONDITIONS,
  name: 'Market Conditions',
  owner: { id: 'revelio-labs', username: 'revelio-labs' },
  tabs: [
    {
      id: RevelioPageId.INDUSTRY_MARKET_CONDITIONS,
      label: 'Market Conditions',
      isActive: true,
      tabContent,
    },
  ],
};
